/* eslint-disable jsx-a11y/label-has-associated-control */
import apiRequest from 'api/apiRequest';
import endpoints from 'api/endpoints';
import { AppBundle } from 'api/optimalprint-sdk.d';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { ButtonNativeApp } from 'store/landing/types';
import useIsAppleDevice from 'hooks/useIsAppleDevice';
import { useLandingPageContext } from 'context/LandingPageContext';
import { countriyCodes } from './constants';
import SendSmsMessageResult from './SendSmsMessageResult/SendSmsMessageResult';
import styles from './DownloadApp.module.scss';
import DownloadAppMobile from './DownloadAppMobile/DownloadAppMobile';

export interface DownloadAppProps {
  h1: string;
  h1Desktop: string;
  h1Mobile: string;
  h1Tablet: string;
  h2: string;
  h2Desktop: string;
  h2Mobile: string;
  h2Tablet: string;
  h3: string;
  h3Desktop: string;
  h3Mobile: string;
  h3Tablet: string;
  name: string;
  phoneAndroidImageUrl: string;
  phoneImageUrl: string;
  sendAnotherSms: string;
  smsButtonLabel: string;
  buttonNativeApp?: ButtonNativeApp;
  layout: string;
}

interface SendSmsParams {
  type: string;
  locale: string;
  countryPrefix: number;
  phoneNumber: number;
  isAppleDevice: boolean;
  bodyKey?: string;
}

const DownloadApp: FC<DownloadAppProps> = ({
  h1,
  h1Mobile,
  h2,
  h3,
  phoneImageUrl,
  sendAnotherSms,
  smsButtonLabel,
  buttonNativeApp,
  phoneAndroidImageUrl,
  layout,
}) => {
  const [prefix, setPrefix] = useState<number>();
  const [phoneNumber, setPhoneNumber] = useState<number>();
  const [success, setSuccess] = useState<boolean>();
  const [message, setMessage] = useState<string>('');
  const [focus, setFocus] = useState(false);
  const { isDesktopDevice, locale } = useLandingPageContext();
  const isAppleDevice = useIsAppleDevice();
  const { t } = useTranslation();

  const sendSms = async () => {
    const params: SendSmsParams = {
      type: 'AppDownloadAppLink',
      locale,
      countryPrefix: prefix || 0,
      phoneNumber: phoneNumber || 0,
      isAppleDevice,
    };

    try {
      const { message: responseMessage } = (await apiRequest(
        endpoints.communicationV1SmsSend,
        params,
        'POST',
        'data',
      )) as AppBundle.Api.Response.Communication.V1.ICommunicationSmsSendV1Response;
      setSuccess(true);
      setMessage(responseMessage);
    } catch (e) {
      setMessage('There was an error while sending the message!');
      setSuccess(false);
      // eslint-disable-next-line no-console
      console.log('Exception logged.');
    }
  };

  const handlePrefixChange = (phonePrefix: string) => {
    setPrefix(parseInt(phonePrefix, 10));
  };

  const handlePhoneNumberChange = (phone: string) => {
    setPhoneNumber(parseInt(phone, 10));
  };

  const handleSendAnotherSms = () => {
    setMessage('');
  };

  return isDesktopDevice ? (
    <div className={cn(styles.container, { [styles.containerWide]: layout === 'wide' })}>
      <div className={styles.contentBlock}>
        <div className={styles.image}>
          <div style={{ backgroundImage: `url(${phoneImageUrl})` }} />
        </div>
        <div className={styles.details}>
          <div className={styles.h1} dangerouslySetInnerHTML={{ __html: h1 }} />
          <h2 dangerouslySetInnerHTML={{ __html: h2 }} />
          {!message && (
            <div className={styles.smsInputs}>
              <div className={styles.inputs}>
                <div className={styles.countryCode}>
                  <select name="countryCode" id="countryCode" onChange={(e) => handlePrefixChange(e.target.value)}>
                    {countriyCodes.map((code) => (
                      // eslint-disable-next-line jsx-a11y/control-has-associated-label
                      <option
                        data-country-id={code.id}
                        key={code.id}
                        value={code.value}
                        id={code.id}
                        dangerouslySetInnerHTML={{ __html: code.label }}
                      />
                    ))}
                  </select>
                </div>

                <div className={styles.phoneNumber}>
                  <input
                    autoComplete="off"
                    name="phone_number"
                    id="opPhoneNumber"
                    onFocus={() => setFocus(true)}
                    onBlur={() => setFocus(false)}
                    onChange={(e) => handlePhoneNumberChange(e.target.value)}
                  />
                  <label htmlFor="opPhoneNumber" className={cn({ [styles.active]: focus })}>
                    {t('txt_mobile')}
                  </label>
                </div>
              </div>
              <button className={styles.sendSmsButton} onClick={sendSms}>
                {smsButtonLabel}
              </button>
            </div>
          )}
          <SendSmsMessageResult message={message} success={success} />
          {message && (
            <div className={styles.sendAnotherSms} onClick={handleSendAnotherSms}>
              {sendAnotherSms}
            </div>
          )}
          <h3 className={styles.h2} dangerouslySetInnerHTML={{ __html: h3 }} />
        </div>
      </div>
    </div>
  ) : (
    <DownloadAppMobile
      h1={h1Mobile}
      buttonNativeApp={buttonNativeApp}
      phoneAndroidImageUrl={phoneAndroidImageUrl}
      phoneImageUrl={phoneImageUrl}
    />
  );
};

export default DownloadApp;
